import { Divider, Button, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import "./MessageDetail.scss";
import { MessageDetailType, MenuProps } from "./types";
import ImagePreviewModal from "./utils/ImagePreviewModal";
import FileDownload from "./utils/FileDownload";

import { header } from "../utils/salesAdminHeader";

import ContentTitle from "../../../components/ui/ContentTitle";
import { apiCall } from "../../../utils/apiCall";
import GeneralModal from "../../../utils/GeneralModal";

//Permission usage
import { useReactiveVar } from "@apollo/client";
import { __currentUser__ } from "../../../graphql/policies";
import Spinner from "../../../components/layout/spinner/Spinner";
import NoPermission from "../../../components/others/NoPermission";
import { useGetUserProgramsByServiceQuery } from "../../../graphql/operations/get-user-program-by-service";
import { ServiceTypes } from "../../../types/types";
import { renderDecodedId } from "../../../utils/renderDecodedId";
import { renderAntDMessageConfig } from "../../../utils/renderAntDMessageConfig";

const MessageDetail = ({ setSelectedService, setSelectSubItem }: MenuProps) => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [details, setDetails] = useState<MessageDetailType>();
  const [modalOpen, setModalOpen] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const currentUser = useReactiveVar(__currentUser__);

  const {
    data: userPrograms,
    loading: userProgramsLoading,
    error: userProgramsError,
  } = useGetUserProgramsByServiceQuery({
    variables: {
      userId: parseInt(renderDecodedId(currentUser?.id)),
      serviceId: 5,
    },
  });

  const fetchDetail = async () => {
    const data = await apiCall({
      method: "GET",
      header: header,
      endPoint:
        process.env.REACT_APP_SALES_ADMIN + `/contents/admin/mails/${id}`,
    });
    if (data) {
      setDetails(data);
    }
  };

  const handleDelete = async () => {
    await apiCall({
      method: "PATCH",
      header: header,
      payload: { ...(id && { mailIds: [parseInt(id)] }) },
      endPoint:
        process.env.REACT_APP_SALES_ADMIN + "/contents/admin/mails/deleted",
    });
    navigate("/sales188/message-box");
  };

  useEffect(() => {
    if (id) {
      fetchDetail();
    }
    setSelectedService(ServiceTypes.SALES_188);
    setSelectSubItem("업무 관리");
  }, [id]);

  if (userProgramsError) {
    message.error(
      renderAntDMessageConfig(
        "Something went wrong. Please contact your system administrator."
      )
    );
  }

  if (userProgramsLoading) {
    return <Spinner />;
  }

  if (
    userPrograms &&
    !userPrograms.wf_adminFirst_afUserProgram?.edges?.find(
      (item) => item.node.afProgram?.programName === "Business Management"
    )
  ) {
    return <NoPermission />;
  }

  return (
    <div id="messageDetailWrapper">
      <ContentTitle
        title="메세지함"
        returnTitle="메세지함"
        onReturn={() => {
          navigate(-1);
        }}
      />

      <div className="content-container">
        <div className="flex justify-start">
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              setModalOpen(true);
            }}
          >
            삭제
          </Button>
        </div>
        <div className="mt-5 info-bar flex justify-between items-center text-center">
          <div className="flex items-center w-3/4">
            <p className="w-2/3 font-bold text-xl text-start pl-3">
              {details?.title}
            </p>
          </div>
          <div className="flex justify-end">
            <p className="pr-5">
              {details?.receiver}({details?.companyNumber})
            </p>
            <p className="pr-5">
              {details?.sendDate ? details?.sendDate.split("T")[0] : ""}
            </p>
            <p className="pr-5">{details?.isRead ? "읽음" : "읽지않음"}</p>
          </div>
        </div>
        <div className="flex flex-col items-center">
          <div
            className="w-3/4 mt-5 body-area"
            dangerouslySetInnerHTML={{ __html: details?.body || "" }}
          />
          <div className="flex flex-col items-center w-2/3 mt-5">
            {details?.blobImageUrls?.map((url, index) => (
              <img
                key={index}
                src={url}
                alt={`announcement-${index}`}
                onClick={() => setPreviewImageUrl(url)}
                style={{ cursor: "pointer" }}
                className="my-2 attached-image"
              />
            ))}
            {details &&
              (details?.blobFiles.length > 0 ||
                details.attachments.length > 0) && (
                <div className="divider">
                  <Divider orientation="left">Attached File</Divider>
                </div>
              )}
            {details?.blobFiles?.map((file, index) => (
              <FileDownload
                key={`blob-${index}`}
                fileName={file.fileName}
                fileUrl={file.fileUrl}
                index={index}
              />
            ))}
            {details?.attachments?.map((file, index) => (
              <FileDownload
                key={`attachment-${index}`}
                fileName={file.fileName}
                base64={file.data}
                contentType={file.contentType}
                fileId={file.fileId}
                index={index}
              />
            ))}
          </div>
        </div>
      </div>
      <GeneralModal
        open={modalOpen}
        close={() => {
          setModalOpen(false);
        }}
        width={400}
        isDeletion={false}
        isConfirm
        noBtnText="취소"
        confirmBtnText="확인"
        title="이 메세지를 삭제할까요?"
        text="삭제된 메세지는 복구할 수 없습니다."
        handleConfirm={handleDelete}
      />
      <ImagePreviewModal
        url={previewImageUrl}
        close={() => setPreviewImageUrl(null)}
      />
    </div>
  );
};

export default MessageDetail;
